const getVantessioCouponSaving = (cart, code) => {
  if (!cart) return { amount: 0, itemTitle: '' };

  let amount = 0;
  (cart.cart_level_discount_applications || []).forEach((discount) => {
    if (discount.type === 'discount_code' && discount.title?.toLowerCase() === code.toLowerCase()) {
      amount += Number(discount.total_allocated_amount || 0);
    }
  });
  (cart.items || []).forEach((item) => {
    (item.line_level_discount_allocations || []).forEach((allocation) => {
      if (allocation.discount_application?.title?.toLowerCase() === code.toLowerCase()) {
        amount += Number(allocation.amount || 0);
      }
    });
  });

  return { amount, itemTitle: cart.items?.[0]?.product_title || '' };
};

const updateVantessioCartCouponNotice = (code, mode, cart = null) => {
  const saving = getVantessioCouponSaving(cart, code);
  document.querySelectorAll('[data-vantessio-cart-coupon-notice]').forEach((notice) => {
    if (!(notice instanceof HTMLElement)) return;

    notice.hidden = false;
    notice.replaceChildren();

    const text = document.createTextNode('Coupon ');
    const codeElement = document.createElement('strong');
    codeElement.textContent = code;
    if (mode === 'active' && saving.amount > 0 && saving.itemTitle) {
      const amountElement = document.createElement('strong');
      amountElement.textContent = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: cart?.currency || 'INR',
        maximumFractionDigits: 2,
      }).format(saving.amount / 100);
      notice.replaceChildren(
        document.createTextNode('You got '),
        amountElement,
        document.createTextNode(' off on '),
        Object.assign(document.createElement('strong'), { textContent: saving.itemTitle }),
        document.createTextNode(' with '),
        codeElement,
        document.createTextNode('.'),
      );
      return;
    }

    const suffix = document.createTextNode(
      mode === 'active'
        ? ' is active and will be applied automatically at checkout.'
        : ' is saved and will be applied automatically when you add an item.',
    );
    notice.append(text, codeElement, suffix);
  });
};

const hideVantessioCartCouponNotice = () => {
  document.querySelectorAll('[data-vantessio-cart-coupon-notice]').forEach((notice) => {
    if (notice instanceof HTMLElement) notice.hidden = true;
  });
};

class VantessioCouponBlock {
  constructor(element) {
    this.element = element;
    this.applyState = element.querySelector('.vantessio-coupon-block__apply-state');
    this.successState = element.querySelector('.vantessio-coupon-block__success-state');
    this.errorState = element.querySelector('.vantessio-coupon-block__error-state');
    this.appliedMessage = element.querySelector('.vantessio-coupon-block__success-message--applied');
    this.pendingMessage = element.querySelector('.vantessio-coupon-block__success-message--pending');
    this.button = element.querySelector('.vantessio-coupon-block__copy-button button');
    this.code = (element.dataset.coupon || '').trim();
    this.storageKey = `vantessio-coupon-applied:${this.code}:${window.location.pathname}`;
    this.pendingStorageKey = `vantessio-coupon-pending:${this.code}`;
    this.activeRequest = null;

    if (this.button) {
      this.apply = this.apply.bind(this);
      this.button.addEventListener('click', this.apply);
    }

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    document.addEventListener('cart:update', this.handleCartUpdate);
    this.restoreCouponState();
  }

  async getCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Unable to read the current cart.');
    return response.json();
  }

  async updateCartDiscount(discountCodes) {
    const sectionIds = [
      ...document.querySelectorAll('[data-section-id]'),
    ]
      .map((element) => element.dataset.sectionId)
      .filter((sectionId, index, all) => sectionId && all.indexOf(sectionId) === index);
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        discount: discountCodes.join(','),
        ...(sectionIds.length > 0 ? { sections: sectionIds, sections_url: window.location.pathname } : {}),
      }),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to apply the discount.');
    return data;
  }

  refreshCartSections(data) {
    if (!data?.sections) return;
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          data: { didError: false, sections: data.sections },
        },
      }),
    );
  }

  hasStoredValue(key) {
    try {
      return window.sessionStorage.getItem(key) === 'true';
    } catch (error) {
      return false;
    }
  }

  setStoredValue(key, value) {
    try {
      if (value) window.sessionStorage.setItem(key, 'true');
      else window.sessionStorage.removeItem(key);
    } catch (error) {
      // The cart remains the source of truth if session storage is unavailable.
    }
  }

  showSuccessState(mode = 'applied') {
    if (!this.applyState || !this.successState) return;
    this.applyState.hidden = true;
    this.successState.hidden = false;
    if (this.appliedMessage) this.appliedMessage.hidden = mode !== 'applied';
    if (this.pendingMessage) this.pendingMessage.hidden = mode !== 'pending';
    if (this.errorState) this.errorState.hidden = true;
  }

  showError(message) {
    if (!this.errorState) return;
    this.errorState.textContent = message;
    this.errorState.hidden = false;
  }

  showApplyState() {
    if (!this.applyState || !this.successState) return;
    this.applyState.hidden = false;
    this.successState.hidden = true;
    if (this.button) {
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
    }
  }

  async applyToCart() {
    const cart = await this.getCart();

    if (cart.item_count === 0) {
      this.setStoredValue(this.pendingStorageKey, true);
      this.showSuccessState('pending');
      updateVantessioCartCouponNotice(this.code, 'pending', cart);
      return false;
    }

    const existingDiscounts = (cart.discount_codes || [])
      .map((discount) => discount.code)
      .filter(Boolean);
    const existingDiscount = (cart.discount_codes || []).find(
      (discount) => discount.code?.toLowerCase() === this.code.toLowerCase(),
    );
    const alreadyApplied = existingDiscount && existingDiscount.applicable !== false;

    if (!alreadyApplied) {
      const discountCodes = existingDiscount
        ? existingDiscounts.filter((discount) => discount.toLowerCase() !== this.code.toLowerCase())
        : [...existingDiscounts];
      discountCodes.push(this.code);
      const data = await this.updateCartDiscount(discountCodes);
      const appliedDiscount = (data.discount_codes || []).find(
        (discount) => discount.code?.toLowerCase() === this.code.toLowerCase(),
      );

      if (appliedDiscount && appliedDiscount.applicable === false) {
        throw new Error('This discount is not valid for the current cart.');
      }

      this.refreshCartSections(data);
      updateVantessioCartCouponNotice(this.code, 'active', data);
    }

    if (alreadyApplied) updateVantessioCartCouponNotice(this.code, 'active', cart);
    this.setStoredValue(this.storageKey, true);
    this.setStoredValue(this.pendingStorageKey, false);
    this.showSuccessState('applied');
    return true;
  }

  async restoreCouponState() {
    try {
      const cart = await this.getCart();
      const appliedDiscount = (cart.discount_codes || []).find(
        (discount) => discount.code?.toLowerCase() === this.code.toLowerCase(),
      );

      if (appliedDiscount?.applicable === true) {
        this.setStoredValue(this.storageKey, true);
        this.setStoredValue(this.pendingStorageKey, false);
        this.showSuccessState('applied');
        updateVantessioCartCouponNotice(this.code, 'active', cart);
        return;
      }

      this.setStoredValue(this.storageKey, false);

      if (this.hasStoredValue(this.pendingStorageKey)) {
        this.showSuccessState('pending');
        updateVantessioCartCouponNotice(this.code, 'pending', cart);
        if (cart.item_count > 0) await this.applyToCart();
        return;
      }

      this.showApplyState();
      hideVantessioCartCouponNotice();
    } catch (error) {
      console.error('Failed to restore coupon state:', error);
      this.showApplyState();
      hideVantessioCartCouponNotice();
    }
  }

  async handleCartUpdate(event) {
    if (!this.hasStoredValue(this.pendingStorageKey) || event.detail?.data?.didError) return;

    try {
      await this.applyToCart();
    } catch (error) {
      console.error('Failed to apply pending discount after cart update:', error);
      this.showError(error.message || 'Unable to apply this discount right now. Please try again.');
    }
  }

  async apply() {
    if (!this.code || !this.button || this.activeRequest) return;

    this.activeRequest = new AbortController();
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    if (this.errorState) this.errorState.hidden = true;

    try {
      await this.applyToCart();
    } catch (error) {
      console.error('Failed to apply discount:', error);
      this.showError(error.message || 'Unable to apply this discount right now. Please try again.');
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
    } finally {
      this.activeRequest = null;
    }
  }

  destroy() {
    if (this.button && this.apply) this.button.removeEventListener('click', this.apply);
    document.removeEventListener('cart:update', this.handleCartUpdate);
    this.activeRequest?.abort();
  }
}

const initializeVantessioCouponBlocks = (root = document) => {
  root.querySelectorAll('.vantessio-coupon-block').forEach((element) => {
    if (!element.vantessioCouponController) {
      element.vantessioCouponController = new VantessioCouponBlock(element);
    }
  });
};

const resumePendingCouponWithoutProductBlock = async () => {
  if (document.querySelector('.vantessio-coupon-block')) return;

  let pendingCode = '';
  try {
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index) || '';
      if (key.startsWith('vantessio-coupon-pending:')) {
        pendingCode = key.slice('vantessio-coupon-pending:'.length).trim();
        if (pendingCode) break;
      }
    }
  } catch (error) {
    return;
  }

  if (!pendingCode) return;

  try {
    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    const cart = await cartResponse.json();
    if (!cartResponse.ok || cart.item_count === 0) return;

    const existingDiscounts = (cart.discount_codes || [])
      .map((discount) => discount.code)
      .filter(Boolean);
    const existingDiscount = (cart.discount_codes || []).find(
      (discount) => discount.code?.toLowerCase() === pendingCode.toLowerCase(),
    );
    const alreadyApplied = existingDiscount && existingDiscount.applicable !== false;
    let updatedCart = cart;

    if (!alreadyApplied) {
      const discountCodes = existingDiscount
        ? existingDiscounts.filter((discount) => discount.toLowerCase() !== pendingCode.toLowerCase())
        : [...existingDiscounts];
      discountCodes.push(pendingCode);
      const updateResponse = await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ discount: discountCodes.join(',') }),
      });
      const updateData = await updateResponse.json();
      updatedCart = updateData;
      const appliedDiscount = (updateData.discount_codes || []).find(
        (discount) => discount.code?.toLowerCase() === pendingCode.toLowerCase(),
      );
      if (!updateResponse.ok || appliedDiscount?.applicable === false) {
        throw new Error('This discount is not valid for the current cart.');
      }

    }

    updateVantessioCartCouponNotice(pendingCode, 'active', updatedCart);
    window.sessionStorage.removeItem(`vantessio-coupon-pending:${pendingCode}`);
    window.sessionStorage.setItem(`vantessio-coupon-applied:${pendingCode}:${window.location.pathname}`, 'true');
  } catch (error) {
    console.error('Failed to resume pending discount:', error);
  }
};

initializeVantessioCouponBlocks();
resumePendingCouponWithoutProductBlock();

document.addEventListener('shopify:section:load', (event) => {
  initializeVantessioCouponBlocks(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll?.('.vantessio-coupon-block').forEach((element) => {
    element.vantessioCouponController?.destroy();
    delete element.vantessioCouponController;
  });
});
